import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  IProperty,
  PropertiesApiResponse,
} from 'src/pages/Reporting/ReportingFormTypes';
import { getProperties, getPropertyByHomeownerId } from 'src/services/client';

export interface UsePropertiesReturn {
  data: IProperty[] | null;
  loading: boolean;
  error: string | null;
  fetchProperties: () => Promise<void>;

  homeownerData: any[] | null;
  homeownerLoading: boolean;
  homeownerError: string | null;
  fetchHomeownerProperties: (contactId: string | number) => Promise<void>;
}

export const fetchStandardPropertyData = async (
  userId: string,
  roleName: string
): Promise<IProperty[]> => {
  const propertiesResponse: any = await getProperties(userId, roleName);

  if (!propertiesResponse || !propertiesResponse.ok) {
    throw new Error('Failed to fetch properties');
  }

  const propertiesFromApi: PropertiesApiResponse =
    await propertiesResponse.json();
  return propertiesFromApi?.data || [];
};

export const fetchHomeownerPropertyData = async (
  contactId: string | number
): Promise<any[]> => {
  const response: any = await getPropertyByHomeownerId(contactId);

  if (!response || (!response.ok && response.status !== 200)) {
    throw new Error('Failed to fetch homeowner properties');
  }

  const responseJson: any[] = await response.json();
  return responseJson.map((item: any) => ({
    ...item,
    isPrimaryContact: item.primaryContact !== null ? 'Yes' : 'No',
  }));
};

const useProperties = (): UsePropertiesReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const [data, setData] = useState<IProperty[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [homeownerData, setHomeownerData] = useState<any[] | null>(null);
  const [homeownerLoading, setHomeownerLoading] = useState<boolean>(false);
  const [homeownerError, setHomeownerError] = useState<string | null>(null);

  const fetchProperties = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result: IProperty[] = await fetchStandardPropertyData(
        user.id,
        user?.roles[0]?.role?.name
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeownerProperties = async (
    contactId: string | number
  ): Promise<void> => {
    setHomeownerLoading(true);
    setHomeownerError(null);

    try {
      const result: any[] = await fetchHomeownerPropertyData(contactId);
      setHomeownerData(result);
    } catch (err) {
      setHomeownerError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setHomeownerLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    fetchProperties,

    homeownerData,
    homeownerLoading,
    homeownerError,
    fetchHomeownerProperties,
  };
};

export default useProperties;
